import api from "../index";

// ** Type Definitions ** //

/**
 * Campaign types available
 */
export type BlogCampaignType = "topicBased" | "keywordBased" | "seriesBased";

/**
 * Campaign status values
 */
export type BlogCampaignStatus =
  | "draft"
  | "scheduled"
  | "generating"
  | "paused"
  | "completed"
  | "stopped"
  | "failed";

/**
 * Blog slot status
 */
export type BlogSlotStatus = "pending" | "generating" | "completed" | "failed";

/**
 * Topic configuration
 */
export interface TopicConfig {
  mainTopic: string;
  subtopics: string[];
  targetKeywords: string[];
  audience: string;
  tone: "professional" | "conversational" | "technical" | "friendly";
  industry: string;
}

/**
 * Content configuration
 */
export interface ContentConfig {
  wordCountMin: number;
  wordCountMax: number;
  includesFAQ: boolean;
  faqCount: number;
  includesHowTo: boolean;
  includesCaseStudy: boolean;
  includesStatistics: boolean;
  internalLinkCount: number;
  externalLinkCount: number;
  patternBlogId?: string;
}

/**
 * SEO configuration
 */
export interface SeoConfig {
  generateSchema: boolean;
  schemaTypes: string[];
  optimizeForFeaturedSnippets: boolean;
  targetSearchIntent: "informational" | "transactional" | "navigational" | "commercial";
}

/**
 * Generation configuration
 */
export interface BlogGenerationConfig {
  blogCount: number;
  minutesBetweenBlogs: number;
  autoApprove: boolean;
  aiProvider: string;
  aiModel?: string;
  generateCoverImages: boolean;
  coverImageStyle: "gradient" | "placeholder" | "none";
}

/**
 * Taxonomy configuration
 */
export interface TaxonomyConfig {
  category?: string;
  autoCreateCategory: boolean;
  newCategoryName?: string;
  tags: string[];
  autoCreateTags: boolean;
  suggestedTagCount: number;
}

/**
 * Blog title entry
 */
export interface BlogTitleEntry {
  title: string;
  targetKeyword?: string;
  searchIntent?: string;
  selected?: boolean;
  description?: string;
  suggestedAt?: string;
}

/**
 * Title generation request
 */
export interface GenerateTitlesRequest {
  topicConfig: Partial<TopicConfig>;
  contentConfig?: Partial<ContentConfig>;
  count?: number;
}

/**
 * Generated title suggestion
 */
export interface TitleSuggestion {
  title: string;
  targetKeyword: string;
  searchIntent: string;
  description: string;
  selected: boolean;
  suggestedAt: string;
}

/**
 * Generated blog tracking
 */
export interface GeneratedBlog {
  blogId?: string;
  title?: string;
  status: BlogSlotStatus;
  targetKeyword?: string;
  generatedAt?: string;
  generationTime?: number;
  wordCount?: number;
  error?: string;
  seoScore?: number;
  suggestedTags: string[];
  linkedTags: string[];
}

/**
 * Created tag tracking
 */
export interface CreatedTag {
  tagId: string;
  name: string;
  createdAt: string;
}

/**
 * Created category tracking
 */
export interface CreatedCategory {
  categoryId?: string;
  name?: string;
  createdAt?: string;
}

/**
 * Campaign statistics
 */
export interface BlogCampaignStatistics {
  totalBlogs: number;
  pendingBlogs: number;
  completedBlogs: number;
  failedBlogs: number;
  totalWordCount: number;
  averageWordCount: number;
  totalGenerationTime: number;
  averageGenerationTime: number;
  averageSeoScore: number;
  tagsCreated: number;
  lastUpdatedAt?: string;
  currentIndex: number;
  lastHeartbeat?: string;
  lastCheckpoint?: {
    index: number;
    timestamp: string;
    processedCount: number;
  };
  pausedAt?: string;
  pauseReason?: string;
  recoveredAt?: string;
  recoveryAttempts: number;
  error?: string;
}

/**
 * Activity log entry
 */
export interface ActivityLogEntry {
  action: string;
  timestamp: string;
  performedBy?: string;
  details?: Record<string, unknown>;
}

/**
 * Full blog campaign object
 */
export interface BlogCampaign {
  _id: string;
  name: string;
  description?: string;
  campaignType: BlogCampaignType;
  topicConfig: TopicConfig;
  contentConfig: ContentConfig;
  seoConfig: SeoConfig;
  status: BlogCampaignStatus;
  scheduleType: "immediate" | "scheduled";
  scheduledFor?: string;
  scheduledEndDate?: string;
  timezone: string;
  generationConfig: BlogGenerationConfig;
  taxonomyConfig: TaxonomyConfig;
  blogTitles: BlogTitleEntry[];
  generatedBlogs: GeneratedBlog[];
  createdTags: CreatedTag[];
  createdCategory?: CreatedCategory;
  statistics: BlogCampaignStatistics;
  activityLog: ActivityLogEntry[];
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Campaign creation payload
 */
export interface CreateBlogCampaignPayload {
  name: string;
  description?: string;
  campaignType: BlogCampaignType;
  topicConfig?: Partial<TopicConfig>;
  contentConfig?: Partial<ContentConfig>;
  seoConfig?: Partial<SeoConfig>;
  scheduleType?: "immediate" | "scheduled";
  scheduledFor?: string;
  scheduledEndDate?: string;
  timezone?: string;
  generationConfig?: Partial<BlogGenerationConfig>;
  taxonomyConfig?: Partial<TaxonomyConfig>;
  blogTitles?: BlogTitleEntry[];
}

/**
 * Paginated response
 */
export interface PaginatedBlogCampaignsResponse {
  campaigns: BlogCampaign[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Campaign progress response
 */
export interface BlogCampaignProgressResponse {
  status: BlogCampaignStatus;
  statistics: BlogCampaignStatistics;
  createdCategory?: CreatedCategory;
  blogs: {
    title?: string;
    status: BlogSlotStatus;
    wordCount?: number;
    seoScore?: number;
    generatedAt?: string;
    error?: string;
  }[];
  recentActivity: ActivityLogEntry[];
}

/**
 * Dashboard stats response
 */
export interface BlogCampaignDashboardStatsResponse {
  total: number;
  generating: number;
  completed: number;
  failed: number;
  totalBlogsGenerated: number;
  recentCampaigns: {
    name: string;
    status: BlogCampaignStatus;
    statistics: BlogCampaignStatistics;
    createdAt: string;
    topicConfig?: {
      mainTopic: string;
    };
  }[];
}

/**
 * Campaign action response
 */
export interface BlogCampaignActionResponse {
  success: boolean;
  message?: string;
  campaign: BlogCampaign;
}

// ** Campaign Management API ** //

/**
 * Creates a new blog campaign
 * @param values Campaign configuration data
 */
export const createBlogCampaign = (values: CreateBlogCampaignPayload) => {
  return api.post("/blog-campaign", values);
};

/**
 * Gets paginated blog campaigns with optional filters
 * @param page Page number
 * @param perPage Items per page
 * @param status Optional status filter
 * @param search Optional search term
 */
export const getBlogCampaignsPaginated = async (
  page: number = 1,
  perPage: number = 10,
  status?: string,
  search?: string
): Promise<PaginatedBlogCampaignsResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });

  if (status) params.append("status", status);
  if (search) params.append("search", search);

  return await api.get(`/blog-campaigns?${params.toString()}`);
};

/**
 * Gets a specific blog campaign by ID
 * @param id Campaign ID
 */
export const getBlogCampaignById = (id: string): Promise<BlogCampaign> => {
  return api.get(`/blog-campaign/${id}`);
};

/**
 * Updates an existing blog campaign (only draft/scheduled status)
 * @param id Campaign ID
 * @param values Updated campaign data
 */
export const updateBlogCampaign = (id: string, values: Partial<CreateBlogCampaignPayload>) => {
  return api.put(`/blog-campaign/${id}`, values);
};

/**
 * Deletes a blog campaign
 * @param id Campaign ID
 */
export const deleteBlogCampaign = (id: string) => {
  return api.delete(`/blog-campaign/${id}`);
};

/**
 * Gets dashboard statistics for blog campaigns
 */
export const getBlogCampaignDashboardStats = (): Promise<BlogCampaignDashboardStatsResponse> => {
  return api.get("/blog-campaigns/stats");
};

// ** Campaign Execution API ** //

/**
 * Starts a blog campaign
 * @param id Campaign ID
 */
export const startBlogCampaign = (id: string): Promise<BlogCampaignActionResponse> => {
  return api.post(`/blog-campaign/${id}/start`);
};

/**
 * Pauses an active blog campaign
 * @param id Campaign ID
 */
export const pauseBlogCampaign = (id: string): Promise<BlogCampaignActionResponse> => {
  return api.post(`/blog-campaign/${id}/pause`);
};

/**
 * Resumes a paused blog campaign
 * @param id Campaign ID
 */
export const resumeBlogCampaign = (id: string): Promise<BlogCampaignActionResponse> => {
  return api.post(`/blog-campaign/${id}/resume`);
};

/**
 * Stops a blog campaign permanently
 * @param id Campaign ID
 */
export const stopBlogCampaign = (id: string): Promise<BlogCampaignActionResponse> => {
  return api.post(`/blog-campaign/${id}/stop`);
};

/**
 * Gets real-time blog campaign progress
 * @param id Campaign ID
 */
export const getBlogCampaignProgress = (id: string): Promise<BlogCampaignProgressResponse> => {
  return api.get(`/blog-campaign/${id}/progress`);
};

// ** Title Generation API ** //

/**
 * Generates title suggestions for blog campaign
 * Called during creation wizard before campaign is saved
 * @param request Topic config and count
 */
export const generateBlogTitles = (
  request: GenerateTitlesRequest
): Promise<{ success: boolean; titles: TitleSuggestion[] }> => {
  return api.post("/blog-campaign/generate-titles", request);
};

/**
 * Confirms selected titles for a campaign
 * @param campaignId Campaign ID
 * @param selectedTitles Array of selected titles
 */
export const confirmBlogTitles = (
  campaignId: string,
  selectedTitles: TitleSuggestion[]
): Promise<BlogCampaignActionResponse> => {
  return api.post(`/blog-campaign/${campaignId}/confirm-titles`, { selectedTitles });
};
